import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { WEEKDAYS, toDateInputValue } from "../../../utils/appointmentLabels";
import {
  Button,
  Field,
  Input,
  Select,
  useToast,
} from "../../ui";
import AdminDoctorSlotPreview from "./AdminDoctorSlotPreview";
import AdminDoctorManualSlots from "./AdminDoctorManualSlots";
import AdminDoctorVacationPeriod from "./AdminDoctorVacationPeriod";
import AdminDoctorScheduleHelp from "./AdminDoctorScheduleHelp";
import {
  filterAssignedBranches,
  pickDefaultBranchId,
  doctorBranchOptionsFromResponse,
} from "./doctorConstants";
import s from "../clinic.module.css";

const DEFAULT_SCHEDULE = [1, 2, 3, 4, 5].map((day) => ({
  day_of_week: day,
  start_time: "09:00",
  end_time: "18:00",
  week_parity: "",
}));

export default function AdminDoctorSchedule({
  doctorId,
  doctorBranchOptions,
  onScheduleSaved,
}) {
  const toast = useToast();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [scheduleForm, setScheduleForm] = useState(DEFAULT_SCHEDULE);
  const [overrides, setOverrides] = useState([]);
  const [overrideDate, setOverrideDate] = useState(toDateInputValue());
  const [overrideKind, setOverrideKind] = useState("extra");
  const [overrideStart, setOverrideStart] = useState("10:00");
  const [overrideEnd, setOverrideEnd] = useState("14:00");
  const [manualDate, setManualDate] = useState(toDateInputValue());
  const [dupTargetBranch, setDupTargetBranch] = useState("");
  const [dupTargetDoctor, setDupTargetDoctor] = useState("");
  const [allDoctors, setAllDoctors] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const applyBranches = (options) => {
      const list = filterAssignedBranches(options);
      setBranches(list);
      setBranchId((prev) =>
        list.some((b) => b.id === prev) ? prev : pickDefaultBranchId(list),
      );
    };

    if (doctorBranchOptions?.length) {
      applyBranches(doctorBranchOptions);
      return;
    }
    if (!doctorId) return;

    clinicAPI
      .getDoctor(doctorId)
      .then((r) => applyBranches(doctorBranchOptionsFromResponse(r.doctor)))
      .catch(() => toast.error("Не удалось загрузить залы тренера"));
  }, [doctorId, doctorBranchOptions, toast]);

  useEffect(() => {
    clinicAPI.getDoctors({ include_summary: "false" }).then((r) => {
      setAllDoctors(r.doctors || []);
    });
  }, []);

  useEffect(() => {
    if (!doctorId || !branchId) return;
    clinicAPI
      .getSchedule(doctorId, branchId)
      .then((r) => {
        const list = r.schedules || [];
        setOverrides(r.overrides || []);
        if (list.length) {
          setScheduleForm(
            list.map((x) => ({
              day_of_week: x.day_of_week,
              start_time: String(x.start_time).slice(0, 5),
              end_time: String(x.end_time).slice(0, 5),
              week_parity:
                x.week_parity != null ? String(x.week_parity) : "",
            })),
          );
        } else {
          setScheduleForm(DEFAULT_SCHEDULE);
        }
      })
      .catch(() => toast.error("Не удалось загрузить расписание"));
  }, [doctorId, branchId, toast]);

  const saveWeekly = async () => {
    if (!branchId) {
      toast.error("Выберите зал");
      return;
    }
    try {
      await clinicAPI.setSchedule(doctorId, branchId, scheduleForm);
      toast.success("Расписание сохранено");
      onScheduleSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const addOverride = async () => {
    if (!branchId) {
      toast.error("Выберите зал");
      return;
    }
    try {
      await clinicAPI.addScheduleOverride(doctorId, {
        branch_id: branchId,
        date: overrideDate,
        kind: overrideKind,
        start_time: overrideKind === "extra" ? overrideStart : null,
        end_time: overrideKind === "extra" ? overrideEnd : null,
      });
      const r = await clinicAPI.getSchedule(doctorId, branchId);
      setOverrides(r.overrides || []);
      toast.success("Исключение добавлено");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const removeOverride = async (id) => {
    await clinicAPI.deleteScheduleOverride(doctorId, id);
    const r = await clinicAPI.getSchedule(doctorId, branchId);
    setOverrides(r.overrides || []);
    toast.success("Удалено");
  };

  const duplicateSchedule = async () => {
    const targetBranch = dupTargetBranch || branchId;
    const targetDoctor = dupTargetDoctor || doctorId;
    const targetBranchName =
      branches.find((b) => b.id === targetBranch)?.name || "";
    const targetDoctorName =
      targetDoctor === doctorId
        ? "этот тренер"
        : allDoctors.find((d) => d.id === targetDoctor)?.full_name || "тренер";

    if (
      targetBranch === branchId &&
      targetDoctor === doctorId
    ) {
      toast.error("Выберите другой зал или другого тренера");
      return;
    }

    try {
      await clinicAPI.duplicateDoctorSchedule(doctorId, {
        source_branch_id: branchId,
        target_branch_id: targetBranch,
        target_doctor_id: targetDoctor,
      });
      toast.success(
        `Шаблон скопирован: ${targetDoctorName} · ${targetBranchName}`,
      );
      onScheduleSaved?.(targetDoctor);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const branchName =
    branches.find((b) => b.id === branchId)?.name || "зал";

  if (!doctorId) return null;

  return (
    <div className={s.scheduleLayout}>
      <div className={s.scheduleBranchBar}>
        <Field label="Зал для расписания">
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <AdminDoctorScheduleHelp />

      <AdminDoctorSlotPreview
        doctorId={doctorId}
        branchId={branchId}
        branches={branches}
      />

      <div className={s.scheduleMainGrid}>
        <div className={s.docSection}>
          <h4 className={s.docSectionTitle}>Недельный шаблон</h4>
          <p className={s.docSectionHint}>
            Повторяется каждую неделю в «{branchName}». Несколько строк на один
            день = перерыв (например 8–12 и 14–18).
          </p>

          {branchId ? (
            <>
              <div className={s.intervalTableHead}>
                <span>День</span>
                <span>Неделя</span>
                <span>Начало</span>
                <span>Конец</span>
                <span />
              </div>
              <div className={s.intervalList}>
                {scheduleForm.map((row, idx) => (
                  <div key={idx} className={s.intervalRow}>
                    <Select
                      className={s.intervalDay}
                      value={row.day_of_week}
                      onChange={(e) => {
                        const next = [...scheduleForm];
                        next[idx].day_of_week = Number(e.target.value);
                        setScheduleForm(next);
                      }}
                    >
                      {WEEKDAYS.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.label}
                        </option>
                      ))}
                    </Select>
                    <Select
                      className={s.intervalParity}
                      value={row.week_parity ?? ""}
                      onChange={(e) => {
                        const next = [...scheduleForm];
                        next[idx].week_parity = e.target.value;
                        setScheduleForm(next);
                      }}
                    >
                      <option value="">Каждую</option>
                      <option value="0">Чётные</option>
                      <option value="1">Нечётные</option>
                    </Select>
                    <Input
                      type="time"
                      value={row.start_time}
                      onChange={(e) => {
                        const next = [...scheduleForm];
                        next[idx].start_time = e.target.value;
                        setScheduleForm(next);
                      }}
                    />
                    <span className={s.intervalDash}>—</span>
                    <Input
                      type="time"
                      value={row.end_time}
                      onChange={(e) => {
                        const next = [...scheduleForm];
                        next[idx].end_time = e.target.value;
                        setScheduleForm(next);
                      }}
                    />
                    <button
                      type="button"
                      className={s.intervalRemove}
                      aria-label="Удалить"
                      onClick={() =>
                        setScheduleForm(scheduleForm.filter((_, i) => i !== idx))
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className={s.scheduleToolbar}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setScheduleForm([
                      ...scheduleForm,
                      {
                        day_of_week: 1,
                        start_time: "09:00",
                        end_time: "18:00",
                        week_parity: "",
                      },
                    ])
                  }
                >
                  + Интервал
                </Button>
                <Button type="button" size="sm" onClick={saveWeekly}>
                  Сохранить шаблон
                </Button>
              </div>
            </>
          ) : null}
        </div>

        <div className={s.docSection}>
          <h4 className={s.docSectionTitle}>Исключения</h4>
          <p className={s.docSectionHint}>
            Выходной или доп. часы на конкретную дату
          </p>
          <div className={s.exceptionForm}>
            <Field label="Дата">
              <Input
                type="date"
                value={overrideDate}
                onChange={(e) => setOverrideDate(e.target.value)}
              />
            </Field>
            <Field label="Тип">
              <div className={s.kindToggle}>
                <button
                  type="button"
                  className={
                    overrideKind === "extra" ? s.kindBtnActive : s.kindBtn
                  }
                  onClick={() => setOverrideKind("extra")}
                >
                  Доп. занятие
                </button>
                <button
                  type="button"
                  className={
                    overrideKind === "closed" ? s.kindBtnActive : s.kindBtn
                  }
                  onClick={() => setOverrideKind("closed")}
                >
                  Выходной
                </button>
              </div>
            </Field>
            {overrideKind === "extra" && (
              <div className={s.exceptionTimes}>
                <Field label="С">
                  <Input
                    type="time"
                    value={overrideStart}
                    onChange={(e) => setOverrideStart(e.target.value)}
                  />
                </Field>
                <Field label="До">
                  <Input
                    type="time"
                    value={overrideEnd}
                    onChange={(e) => setOverrideEnd(e.target.value)}
                  />
                </Field>
              </div>
            )}
            <Button
              type="button"
              size="sm"
              onClick={addOverride}
              disabled={!branchId}
            >
              Добавить
            </Button>
          </div>
          {overrides.length > 0 ? (
            <ul className={s.exceptionList}>
              {overrides.slice(0, 12).map((o) => (
                <li
                  key={o.id}
                  className={
                    o.kind === "closed"
                      ? s.exceptionItemClosed
                      : s.exceptionItemExtra
                  }
                >
                  <span>
                    {new Date(`${o.date}T12:00:00`).toLocaleDateString("ru-RU", {
                      day: "numeric",
                      month: "short",
                    })}{" "}
                    —{" "}
                    {o.kind === "closed"
                      ? "выходной"
                      : `${String(o.start_time).slice(0, 5)}–${String(o.end_time).slice(0, 5)}`}
                  </span>
                  <button
                    type="button"
                    className={s.exceptionRemove}
                    onClick={() => removeOverride(o.id)}
                  >
                    Удалить
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={s.docEmptyHint}>Нет исключений</p>
          )}

          <AdminDoctorVacationPeriod
            doctorId={doctorId}
            branchId={branchId}
            branches={branches}
            onChanged={() => {
              clinicAPI.getSchedule(doctorId, branchId).then((r) => {
                setOverrides(r.overrides || []);
              });
            }}
          />
        </div>
      </div>

      <div className={s.scheduleAdvanced}>
        <button
          type="button"
          className={s.scheduleAdvancedToggle}
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {showAdvanced ? "▼" : "▶"} Дополнительно: копирование и ручные слоты
        </button>
        {showAdvanced && branchId && (
          <div className={s.scheduleAdvancedBody}>
            <div className={s.docSection}>
              <h4 className={s.docSectionTitle}>Копировать шаблон</h4>
              <p className={s.docSectionHint}>
                Переносит текущий недельный шаблон из «{branchName}» в выбранный
                зал и/или к другому тренеру. Существующий шаблон у цели будет
                заменён.
              </p>
              <div className={s.dupForm}>
                <Field label="Источник">
                  <Input readOnly value={branchName} />
                </Field>
                <Field label="Зал назначения">
                  <Select
                    value={dupTargetBranch || branchId}
                    onChange={(e) => setDupTargetBranch(e.target.value)}
                  >
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                        {b.id === branchId ? " (текущий)" : ""}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Тренер назначения">
                  <Select
                    value={dupTargetDoctor || doctorId}
                    onChange={(e) => setDupTargetDoctor(e.target.value)}
                  >
                    <option value={doctorId}>Этот тренер (текущий)</option>
                    {allDoctors
                      .filter((d) => d.id !== doctorId)
                      .map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.full_name}
                        </option>
                      ))}
                  </Select>
                </Field>
              </div>
              <div className={s.dupActions}>
                <Button type="button" onClick={duplicateSchedule}>
                  Копировать шаблон
                </Button>
              </div>
            </div>
            <AdminDoctorManualSlots
              doctorId={doctorId}
              branchId={branchId}
              date={manualDate}
              onDateChange={setManualDate}
            />
          </div>
        )}
      </div>
    </div>
  );
}
